import React from 'react'
import { Link, graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { ArrowLeftIcon, ArrowRightIcon } from '@radix-ui/react-icons'
import { useAuth0 } from '@auth0/auth0-react'
import config from '../config'
import Layout from '../layouts/layout-one'
import Tags from '../components/Tags'
import Seo from '../components/SEO'
import { useFetchWithToken } from '../utils/useFetch'
import 'katex/dist/katex.min.css'
import { levelName, isExpired } from '../components/User'

const NoContacts = () => (
  <div className="contacts">当前信息还没有联系方式, 请提交错误给管理员</div>
)

const PersonDetails = ({ url }) => {
  const { user, isAuthenticated, getAccessTokenSilently, loginWithRedirect } =
    useAuth0()
  const { data, loading, error } = useFetchWithToken(
    url,
    getAccessTokenSilently
  )
  const level = levelName(user)

  if (!isAuthenticated) {
    return (
      <div className="contacts">
        您需要
        <Link to="#" onClick={loginWithRedirect}>
          登录
        </Link>
        才可以查看联系方式
      </div>
    )
  }

  if (loading) {
    return <div className="contacts">联系方式加载中...</div>
  }

  if (error) {
    return (
      <div className="contacts">
        <div>出现异常, 请刷新重试</div>
        <hr />
        <div>{error}</div>
      </div>
    )
  }

  if (!data) {
    return <div className="contacts">无数据</div>
  }

  if (data.code === 'OK') {
    if (!data.data.contacts || data.data.contacts.length === 0) {
      return <NoContacts />
    }
    return (
      <div className="contacts">
        <ul>
          {data.data.contacts.map((x) => {
            return <li>{x}</li>
          })}
        </ul>
      </div>
    )
  } else if (data.code === 'InsufficientPrivileges') {
    return (
      <div className="contacts">
        您当前的级别{' '}
        <label className="membership">
          {level}
          {isExpired(user) && '(已过期)'}
        </label>{' '}
        无法查看联系方式
        <hr />
        <Link to="/account/">购买会员</Link>
      </div>
    )
  } else if (data.code === 'NotFound') {
    return <NoContacts />
  }
  return <div>{JSON.stringify(data)}</div>
}

const GalleryLazy = React.lazy(() => import('../components/Gallery.js'))

const Post = (props) => {
  const post = props.data.markdownRemark
  const relatedPosts = props.pageContext.related
  let similarPosts = null
  if (relatedPosts && relatedPosts.length > 0) {
    similarPosts = (
      <div className="similar-posts">
        <p>
          <strong>其他推荐:</strong>
        </p>
        <div className="gallery">
          {relatedPosts.map((post, index) => {
            return (
              <Link
                className="gallery-card"
                key={post.node.fields.slug}
                to={post.node.fields.slug}
              >
                <div>
                  <GatsbyImage
                    alt={`post-${index}`}
                    image={
                      post.node.frontmatter.featuredImage?.childImageSharp
                        ?.gatsbyImageData
                    }
                  />
                </div>
                <div className="gallery-title">
                  {post.node.frontmatter.title}
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    )
  }
  const url = `${config.siteMetadata.siteUrl}${post.fields.slug}`
  const prevPost = props.pageContext.prev
  const nextPost = props.pageContext.next
  const detailUrl = `${process.env.LF_API}/loufeng/detail/${post.frontmatter.id}`
  const images = post.frontmatter.images

  return (
    <Layout>
      <Seo
        post={post}
        title={post.frontmatter.title}
        url={url}
        keywords={post.frontmatter.tags.reduce((p, c) => p + ',' + c)}
        description={post.frontmatter.excerpt}
        image={
          post.frontmatter.featuredImage.childImageSharp.gatsbyImageData.src
        }
      />
      <div className="post">
        {images && (
          <React.Suspense fallback={<div>图片加载中</div>}>
            <GalleryLazy images={images} />
          </React.Suspense>
        )}
        <h1 className="title">{post.frontmatter.title}</h1>
        <div
          className="post-content"
          dangerouslySetInnerHTML={{ __html: post.html }}
        />
        <div className="post-content">
          <PersonDetails url={detailUrl} />
        </div>
        <div>
          <small>发表于 {post.frontmatter.date}</small>
        </div>
        <Tags tags={post.frontmatter.tags} />
        <hr />
        <div className="pure-g post-nativation">
          {prevPost && (
            <Link
              to={prevPost.fields.slug}
              className="pure-u-1 pure-u-md-1-2 prev"
            >
              <h4>上一篇</h4>
              <span>
                <ArrowLeftIcon /> {prevPost.frontmatter.title}
              </span>
            </Link>
          )}
          {nextPost && (
            <Link
              to={nextPost.fields.slug}
              className="pure-u-1 pure-u-md-1-2 next"
            >
              <h4>下一篇</h4>
              <span>
                {nextPost.frontmatter.title} <ArrowRightIcon />
              </span>
            </Link>
          )}
        </div>
      </div>
      {similarPosts}
    </Layout>
  )
}

export default Post

export const pageQuery = graphql`
  query ($slug: String!) {
    markdownRemark(fields: { slug: { eq: $slug } }) {
      id
      html
      fields {
        slug
      }
      frontmatter {
        id
        excerpt
        title
        date(formatString: "YYYY MoDo", locale: "zh-cn")
        images {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
            original {
              src
            }
          }
        }
        tags
        featuredImage {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
      }
    }
  }
`
